// src/components/About.js

import React from "react";
import {
  Container,
  Typography,
  Box,
  Grid,
  Button,
  Paper,
  Divider,
  useTheme,
} from "@mui/material";
import { motion } from "framer-motion";
import YouTubeIcon from "@mui/icons-material/YouTube";
import FlightIcon from "@mui/icons-material/Flight";
import HotelIcon from "@mui/icons-material/Hotel";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Helmet } from "react-helmet-async";

const AnimatedSection = ({ children }) => {
  const sectionVariants = {
    hidden: { opacity: 0, y: 40 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };
  return (
    <motion.div
      variants={sectionVariants}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true, amount: 0.15 }}
    >
      {children}
    </motion.div>
  );
};

const TravelImage = ({ src, alt, caption }) => (
  <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
    <Box
      sx={{
        flex: 1,
        minHeight: { xs: 260, md: 0 },
        borderRadius: "16px",
        boxShadow: "0px 8px 32px rgba(0, 0, 0, 0.18)",
        overflow: "hidden",
      }}
    >
      <Box
        component="img"
        src={src}
        alt={alt}
        title={alt}
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "cover",
          objectPosition: "center center",
          display: "block",
        }}
      />
    </Box>
    {caption && (
      <Typography
        variant="caption"
        sx={{
          display: "block",
          mt: 1.5,
          color: "text.disabled",
          fontStyle: "italic",
          textAlign: "center",
        }}
      >
        {caption}
      </Typography>
    )}
  </Box>
);

const About = () => {
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";

  return (
    <>
      <Helmet>
        <title>About FlyBnB | Our Story</title>
        <meta
          name="description"
          content="Meet the budget travelers behind FlyBnB, a tool for comparing flights and Airbnb stays together before you book."
        />
        <meta property="og:title" content="About FlyBnB | Our Story" />
        <meta
          property="og:description"
          content="Built by travelers who plan everything around budget. FlyBnB combines flights and Airbnb into one search so you can see the real cost of any trip."
        />
        <link rel="canonical" href="https://flybnb.io/about" />
      </Helmet>

      <Container maxWidth="md" sx={{ mt: 5, mb: 12 }}>
        {/* ── HERO ── */}
        <AnimatedSection>
          <Box sx={{ textAlign: "center", mb: { xs: 8, md: 12 } }}>
            <Typography
              variant="h2"
              component="h1"
              sx={{
                fontWeight: 800,
                mb: 2,
                background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.stay || "#e91e63"} 90%)`,
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Our Story
            </Typography>
            <Typography
              variant="h5"
              sx={{ color: "text.secondary", mb: 1, fontWeight: 400 }}
            >
              Hey, we’re Mo & Rafa — the people behind FlyBnB.
            </Typography>
            <Typography
              variant="body1"
              sx={{
                color: "text.secondary",
                mb: 4,
                maxWidth: 520,
                mx: "auto",
                lineHeight: 1.8,
              }}
            >
              We travel a lot, we document it on{" "}
              <Box
                component="a"
                href="https://www.youtube.com/@ours2discover"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                <YouTubeIcon
                  sx={{
                    fontSize: 14,
                    verticalAlign: "middle",
                    color: "#FF0000",
                    mr: 0.3,
                  }}
                />
                Ours2Discover
              </Box>
              , and we built this tool because planning trips the old way was
              driving us crazy.
            </Typography>
          </Box>
        </AnimatedSection>

        {/* ── SECTION 1: Budget-first travelers ── */}
        <AnimatedSection>
          <Grid
            container
            spacing={5}
            alignItems="stretch"
            sx={{ mb: { xs: 10, md: 14 } }}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                How it started
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, mt: 0.5 }}>
                We always chose destinations by what we could afford.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                }}
              >
                We don’t pick a place and then figure out the cost. We figure
                out the cost first, then pick the place. Every trip started with
                the same question: where can we go from here, right now, without
                breaking the bank?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                That’s how we’ve ended up in Cappadocia, in the ruin bars of
                Budapest, hiking the Isle of Skye in the rain. Not because we
                had a dream destination list — because the numbers made sense.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TravelImage
                src="/Cappadocia.jpeg"
                alt="Hot air balloons over Cappadocia, Turkey"
                caption="Cappadocia, Turkey — we picked this because flights were surprisingly cheap"
              />
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* ── SECTION 2: Planning chaos ── */}
        <AnimatedSection>
          <Grid
            container
            spacing={5}
            alignItems="center"
            direction={{ xs: "column", md: "row-reverse" }}
            alignItems="stretch"
            sx={{ mb: { xs: 10, md: 14 } }}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                The problem
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, mt: 0.5 }}>
                Planning this way meant endless tabs and spreadsheets.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                }}
              >
                Google Flights for the ticket. Airbnb for the stay. Convert
                currencies. Compare dates. Try a different origin. Try a
                different month. Screenshot everything into a spreadsheet and
                start over.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                The actual trip planning — the fun part — was buried under hours
                of logistics. There had to be a better way to see the real cost
                of going somewhere.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TravelImage
                src="/Prague.jpeg"
                alt="Prague, Czechia"
                caption="Prague — the stays were unbeatable, but finding that took forever"
              />
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* ── INSIGHT CALLOUT: Flights vs. Stays ── */}
        <AnimatedSection>
          <Paper
            elevation={0}
            sx={{
              mb: { xs: 10, md: 14 },
              p: { xs: 3, md: 5 },
              borderRadius: "24px",
              border: `1px solid ${isDark ? "rgba(95,90,162,0.35)" : "rgba(95,90,162,0.2)"}`,
              background: isDark
                ? "linear-gradient(135deg, rgba(28,28,53,0.9) 0%, rgba(20,25,46,1) 100%)"
                : "linear-gradient(135deg, rgba(240,238,255,0.9) 0%, rgba(255,255,255,1) 100%)",
              overflow: "hidden",
              position: "relative",
            }}
          >
            {/* Decorative glow */}
            <Box
              sx={{
                position: "absolute",
                top: -60,
                right: -60,
                width: 200,
                height: 200,
                borderRadius: "50%",
                background:
                  "radial-gradient(circle, rgba(95,90,162,0.15) 0%, transparent 70%)",
                pointerEvents: "none",
              }}
            />

            <Typography
              variant="overline"
              sx={{ color: "primary.main", fontWeight: 700, letterSpacing: 2 }}
            >
              The insight that changed how we travel
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mt: 0.5, mb: 0.5 }}>
              The "expensive" flight is often the cheaper trip.
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", mb: 4 }}>
              We learned this the hard way — optimizing for cheap flights often
              leads to expensive stays. Here’s the math:
            </Typography>

            {/* Comparison cards */}
            <Grid container spacing={2} alignItems="stretch" sx={{ mb: 4 }}>
              {/* Card A — Cheap flight */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    borderRadius: "16px",
                    border: `1px solid ${isDark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)"}`,
                    bgcolor: isDark
                      ? "rgba(255,255,255,0.03)"
                      : "rgba(0,0,0,0.02)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Card label strip */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1,
                      bgcolor: isDark
                        ? "rgba(255,255,255,0.06)"
                        : "rgba(0,0,0,0.05)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <FlightIcon sx={{ fontSize: 15, color: "text.disabled" }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "text.secondary",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Cheap flight approach
                    </Typography>
                  </Box>

                  {/* Line items */}
                  <Box
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          ✈️ Flight
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          Lowest fare
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $220
                      </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          🏠 Airbnb · 7 nights
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          $140/night — high cost area
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $980
                      </Typography>
                    </Box>
                    <Divider sx={{ opacity: 0.3 }} />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        Total
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: "1.25rem",
                          color: "primary.main",
                        }}
                      >
                        $1,200
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>

              {/* Card B — Smarter total */}
              <Grid item xs={12} sm={6}>
                <Box
                  sx={{
                    borderRadius: "16px",
                    border: "1px solid rgba(95,90,162,0.5)",
                    background: isDark
                      ? "linear-gradient(160deg, rgba(95,90,162,0.18) 0%, rgba(95,90,162,0.07) 100%)"
                      : "linear-gradient(160deg, rgba(95,90,162,0.1) 0%, rgba(95,90,162,0.03) 100%)",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    overflow: "hidden",
                  }}
                >
                  {/* Card label strip — green banner */}
                  <Box
                    sx={{
                      px: 2.5,
                      py: 1,
                      background: "linear-gradient(90deg, #2d7a3a, #3a9e4a)",
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                    }}
                  >
                    <HotelIcon
                      sx={{ fontSize: 15, color: "rgba(255,255,255,0.85)" }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#fff",
                        textTransform: "uppercase",
                        letterSpacing: 1,
                      }}
                    >
                      Smarter pick — optimise total cost
                    </Typography>
                  </Box>

                  {/* Line items */}
                  <Box
                    sx={{
                      p: 2.5,
                      display: "flex",
                      flexDirection: "column",
                      gap: 1.5,
                      flex: 1,
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          ✈️ Flight
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          {" "}
                          Pricier flight
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $380
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ opacity: 0.3, borderColor: "rgba(95,90,162,0.4)" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "flex-start",
                      }}
                    >
                      <Box>
                        <Typography
                          variant="body2"
                          sx={{ color: "text.primary", fontWeight: 500 }}
                        >
                          🏠 Airbnb · 7 nights
                        </Typography>
                        <Typography
                          variant="caption"
                          sx={{ color: "text.disabled" }}
                        >
                          $45/night — affordable destination
                        </Typography>
                      </Box>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "text.primary" }}
                      >
                        $315
                      </Typography>
                    </Box>
                    <Divider
                      sx={{ opacity: 0.3, borderColor: "rgba(95,90,162,0.4)" }}
                    />
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 700, color: "success.main" }}
                      >
                        Total
                      </Typography>
                      <Typography
                        sx={{
                          fontWeight: 900,
                          fontSize: "1.25rem",
                          color: "success.main",
                        }}
                      >
                        $695
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {/* Savings summary */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                p: 2.5,
                borderRadius: "14px",
                bgcolor: isDark
                  ? "rgba(46,160,67,0.12)"
                  : "rgba(46,160,67,0.08)",
                border: "1px solid rgba(46,160,67,0.25)",
                mb: 3,
                flexWrap: "wrap",
              }}
            >
              <TrendingDownIcon
                sx={{ color: "success.main", fontSize: 28, flexShrink: 0 }}
              />
              <Box>
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 800,
                    color: "success.main",
                    lineHeight: 1.2,
                  }}
                >
                  You save $505 — by spending more on the flight.
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", mt: 0.3 }}
                >
                  That’s almost enough for another trip.
                </Typography>
              </Box>
              <Box
                sx={{
                  ml: "auto",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  flexShrink: 0,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "primary.main" }}
                >
                  $1,200
                </Typography>
                <ArrowForwardIcon
                  sx={{ color: "text.disabled", fontSize: 18 }}
                />
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 900, color: "success.main" }}
                >
                  $695
                </Typography>
              </Box>
            </Box>

            <Typography
              variant="body1"
              sx={{
                fontSize: "1.05rem",
                lineHeight: 1.85,
                color: "text.secondary",
              }}
            >
              This is the whole reason FlyBnB exists — to show you flight and
              stay costs together so you can make the smarter call, instead of
              optimizing one thing while ignoring the other.
            </Typography>
          </Paper>
        </AnimatedSection>

        {/* ── SECTION 3: Why we built it ── */}
        <AnimatedSection>
          <Grid
            container
            spacing={5}
            alignItems="stretch"
            sx={{ mb: { xs: 10, md: 14 } }}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                Why we built it
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, mt: 0.5 }}>
                We wanted one search. Flight + stay. Real total cost.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                }}
              >
                FlyBnB started as a tool for us — to answer the question "where
                can we go this month?" without spending two hours on it. You
                type your origin, your budget, your dates, and you see what’s
                actually possible.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                Every trip we take is planned with FlyBnB. It’s not a pitch — we actually use it.
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TravelImage
                src="/Budapest.jpeg"
                alt="Budapest, Hungary"
                caption="Budapest — affordable stays, incredible city"
              />
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* ── SECTION 4: More exploring, less planning ── */}
        <AnimatedSection>
          <Grid
            container
            spacing={5}
            alignItems="center"
            direction={{ xs: "column", md: "row-reverse" }}
            alignItems="stretch"
            sx={{ mb: { xs: 10, md: 14 } }}
          >
            <Grid item xs={12} md={6}>
              <Typography
                variant="overline"
                sx={{
                  color: "primary.main",
                  fontWeight: 700,
                  letterSpacing: 2,
                }}
              >
                Our goal
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, mt: 0.5 }}>
                More time exploring. Less time planning.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                }}
              >
                If you’re the kind of person who plans trips around what you can
                afford — not the other way around — FlyBnB was built for you. It
                doesn’t matter if it’s your first big trip or your fiftieth. The
                math should be easy.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                Thanks for being here. It genuinely means a lot that you’re
                supporting something we care about.
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: "1.05rem",
                  lineHeight: 1.85,
                  color: "text.secondary",
                  mt: 2,
                }}
              >
                — Mo & Rafa ✈️
              </Typography>
            </Grid>
            <Grid
              item
              xs={12}
              md={6}
              sx={{ display: "flex", flexDirection: "column" }}
            >
              <TravelImage
                src="/Skye.jpeg"
                alt="Isle of Skye, Scotland"
                caption="Isle of Skye — unexpected, unforgettable, and well within budget"
              />
            </Grid>
          </Grid>
        </AnimatedSection>

        {/* ── CTA ── */}
        <AnimatedSection>
          <Box
            sx={{
              textAlign: "center",
              p: { xs: 4, md: 6 },
              borderRadius: "20px",
              mb: { xs: 10, md: 14 },
              border: `1px solid ${isDark ? "rgba(95,90,162,0.3)" : "rgba(95,90,162,0.2)"}`,
              background: isDark
                ? "linear-gradient(135deg, #1c1c35 0%, #14192e 100%)"
                : "linear-gradient(135deg, #f0eeff 0%, #ffffff 100%)",
            }}
          >
            <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
              Ready to find your next trip?
            </Typography>
            <Typography variant="body1" sx={{ color: "text.secondary", mb: 3 }}>
              Search flights and Airbnb stays together — and see what’s actually
              affordable.
            </Typography>
            <Button
              variant="contained"
              href="/"
              sx={{
                fontWeight: "bold",
                py: 1.5,
                px: 5,
                borderRadius: "12px",
                textTransform: "none",
                fontSize: "1rem",
                background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark || theme.palette.primary.main})`,
              }}
            >
              ✈️ Start Searching
            </Button>
          </Box>
        </AnimatedSection>

        {/* ── YOUTUBE VIDEO ── */}
        <AnimatedSection>
          <Box>
            <Typography
              variant="overline"
              sx={{
                color: "primary.main",
                fontWeight: 700,
                letterSpacing: 2,
                display: "block",
                mb: 0.5,
              }}
            >
              Follow along
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 800, mb: 1 }}>
              We document every trip on YouTube.
            </Typography>
            <Typography
              variant="body1"
              sx={{ color: "text.secondary", mb: 4, lineHeight: 1.8 }}
            >
              Budget destinations, honest travel costs, and the places most
              people overlook — all on{" "}
              <Box
                component="a"
                href="https://www.youtube.com/@ours2discover"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "primary.main",
                  fontWeight: 600,
                  textDecoration: "none",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                Ours2Discover
              </Box>
              .
            </Typography>
            <Box
              sx={{
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
                overflow: "hidden",
                background: "#000",
                borderRadius: "16px",
                boxShadow: "0px 12px 40px rgba(0,0,0,0.3)",
                mb: 3,
              }}
            >
              <iframe
                src="https://www.youtube.com/embed/kjpkt3ot7AM?si=mK0KFv1d0bRIyKzI"
                title="Ours2Discover — Mo and Rafa travel channel"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                style={{
                  border: 0,
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                }}
              />
            </Box>
            <Box sx={{ textAlign: "center" }}>
              <Button
                variant="contained"
                startIcon={<YouTubeIcon />}
                href="https://www.youtube.com/@ours2discover"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  fontWeight: "bold",
                  py: 1.5,
                  px: 4,
                  borderRadius: "12px",
                  textTransform: "none",
                  fontSize: "1rem",
                  backgroundColor: "#FF0000",
                  "&:hover": { backgroundColor: "#c00" },
                }}
              >
                Watch Ours2Discover
              </Button>
            </Box>
          </Box>
        </AnimatedSection>
      </Container>
    </>
  );
};

export default About;
